import React, { useEffect } from 'react';
import axios from 'axios';
import { withOktaAuth } from '@okta/okta-react';
import { withRouter } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState, useAppThunkDispatch } from '../redux/store';
import { resetEncryptedToken, setEncryptedToken, setErrorInfo } from '../redux/common-data-slice';
import { PatientService } from '../services/patient.service';
import { environment } from '../environments/environment';
import { clearBackendToken, readBackendToken, writeBackendToken } from './backendTokenCache';
import { requestSessionTeardown } from './sessionTeardown';
import {
	canRetryAfter401,
	markRetried,
	renewAccessTokenOnce,
	__resetRenewalStateForTests,
} from './oktaTokenRecovery';

interface Idata {
	oktaAuth: any,
	history: any,
	location: any,
	match: any,
	children: any,
	requestInterceptorHandler: any,
	requestInterceptorErrorHandler: any,
	responseInterceptorSuccessHandler: any,
	responseInterceptorErrorHandler: any,
}

let requestInterceptor: any = null;
let responseInterceptor: any = null;
let errStatus: string | null = null
let isSigningOut = false

/**
 * Test seam. `isSigningOut` is a one-way latch in normal operation (the page is
 * navigating away, so it never needs resetting), which makes logout-driven
 * assertions leak across tests. Reset it — and the ERR_NETWORK latch — between
 * cases so each pin observes only its own behavior.
 */
export const __resetAuthRecoveryStateForTests = () => {
	isSigningOut = false
	errStatus = null
	__resetRenewalStateForTests()
}
const UndecoratedSetupAxios: React.FC<any> = ({ oktaAuth, history, location, match,
	children, requestInterceptorHandler = (config: any) => config,
	requestInterceptorErrorHandler = (error: any) => Promise.reject(error),
	responseInterceptorSuccessHandler = (response: any) => response,
	responseInterceptorErrorHandler = (error: any) => Promise.reject(error) }) => {
	const dispatch = useAppThunkDispatch();
	const encryptedToken = useSelector((state: RootState) => state.commonData.encryptedToken.token)
	const errorInfo = useSelector((state: RootState) => state.commonData.errorInfo)
	const patientService = new PatientService()

	useEffect(() => {
		requestInterceptor = axios.interceptors.request.use(
			requestInterceptorSuccessHandler,
			requestInterceptorErrorHandlerFun
		);
		responseInterceptor = axios.interceptors.response.use(
			responseInterceptorSuccessHandlerFun,
			responseInterceptorErrorHandlerFun
		);
		return () => {
			axios.interceptors.request.eject(requestInterceptor);
			axios.interceptors.response.eject(responseInterceptor);
		}
	}, [encryptedToken])


	/**
	 * Return the backend-minted JWE, minting a fresh one if the cache is empty.
	 *
	 * Eviction is NOT on a wall clock here — `backendTokenCache` keeps a 15-minute
	 * backstop, but the real invalidation signal is Okta's `renewed` event (see
	 * okta-token-renewal.component.tsx and okta-navbar.component.tsx). A JWE that
	 * outlives the access token it wraps introspects `active=false` server-side,
	 * which used to surface as a 401 -> immediate logout with no warning dialog.
	 */
	const tokenVerificationAPI = async () => {
		try {
			let encryptedToken = readBackendToken()
			if (!encryptedToken) {
				const res = await patientService.tokenVerification()
				encryptedToken = res.data.token
				writeBackendToken(encryptedToken as string)
				dispatch(setEncryptedToken({ token: encryptedToken, status: 'ready' }))
			}
			return encryptedToken;  // Make sure to return the token
		} catch (error) {
			console.error('Token verification failed:', error)
			throw error;  // Throw the error so it can be handled upstream
		}
	}

	/**
	 * Tear the session down. NOT called directly — every caller goes through
	 * `performLogout`, which gates on whether clinical audio is at risk (CAPA F274).
	 */
	const doLogout = async () => {
		if (isSigningOut) return;
		isSigningOut = true;
		dispatch(resetEncryptedToken());
		// Clear our backend token cache immediately before any async work
		window.sessionStorage.removeItem('encryptedToken');
		window.sessionStorage.removeItem('encryptedTokenTS');
		// Set the flag BEFORE signOut so the authStateManager handler in App.tsx
		// doesn't trigger a concurrent signInWithRedirect() call.
		// The handler will redirect from origin → /login once the flag is seen.
		sessionStorage.setItem('oktaLoggingOut', 'true')
		// Clear storage in beforeunload so:
		// 1. The ID token is still available when signOut() builds the logout URL
		//    (id_token_hint tells Okta which session to close — without it the
		//     server session may survive and SSO auto-signs the user back in).
		// 2. oktaLoggingOut is gone when we return to origin, so authStateManager
		//    fires signInWithRedirect() → Okta shows the login form.
		window.addEventListener('beforeunload', () => {
			localStorage.clear();
			sessionStorage.clear();
		}, { once: true })
		try {
			await oktaAuth.signOut({ postLogoutRedirectUri: window.location.origin });
		} catch (_) {
			// Network error — clear storage and redirect to Okta login directly
			localStorage.clear();
			sessionStorage.clear();
			oktaAuth.signInWithRedirect({ redirectUri: window.location.origin + '/login/callback' });
		}
	};

	/**
	 * The only way this file ends a session (CAPA F274).
	 *
	 * A dead credential is not a reason to destroy a live encounter. `doLogout` registers
	 * a `beforeunload` that clears both storages and navigates away — which unmounts the
	 * recorder and ends the capture. While clinical audio exists only in this tab, the
	 * teardown is HELD and runs once the recording is finished and uploaded.
	 *
	 * This does not resurrect the session — the token is gone either way. It stops the
	 * navigation that turns a recoverable credential race into a lost patient visit.
	 */
	const performLogout = (reason: string) => {
		requestSessionTeardown(doLogout, reason);
	};

	const requestInterceptorSuccessHandler = async (config: any) => {
		if (errStatus !== 'ERR_NETWORK' || errorInfo?.status !== 'ERR_NETWORK') {
			let token = await oktaAuth?.getAccessToken();

			// No Okta access token in storage. This is USUALLY RECOVERABLE, not a dead
			// session: okta-auth-js removes the token the instant it deems it expired,
			// and config.ts sets expireEarlySeconds:300, so it vanishes five minutes
			// before its real exp — typically while AutoRenewService's renewal is still
			// in flight. That service also only runs in the elected leader tab, which
			// may be a backgrounded tab with throttled timers. Renewing once here is
			// what keeps a clinician from being dropped to the login page mid-chart
			// with no countdown dialog. Only a FAILED renewal means the session is gone.
			if (!token && oktaAuth) {
				token = await renewAccessTokenOnce(oktaAuth);
				if (!token) {
					performLogout(`no-access-token-and-renewal-failed (url: ${config?.url ?? 'unknown'})`);
					return Promise.reject(new Error('Session expired'));
				}
				// The cached JWE wraps the token we just replaced — evict it so the
				// mint below wraps the new one.
				clearBackendToken();
			}

			const newConfig = requestInterceptorHandler(config);

			let tokenFilter = environment?.okta?.isEnabled
				// Read the JWE fresh from the cache rather than from the Redux value
				// captured in this closure: the closure only refreshes when the
				// registering effect re-runs, so a renewal-driven eviction would
				// otherwise keep serving the stale JWE for the rest of the page's life.
				? newConfig.url.endsWith('/token-verification') ? token : readBackendToken()
				: environment?.oidc?.isEnabled
					? localStorage.getItem("access_token")
					: 'eyJhbGciOiJkaXIiLCJlbmMiOiJBMjU2R0NNIn0..7-LOy9rUOPP06X3w0rIkyQ.WXdHCygyeEn5iC2Y8tSwa33m199HBIoqS_XIn0EAizo6iWZq95I8p5Xx5Bq309hthEmdRmQC1dkoPBesN6l2XSeqzgNfHimqA1UhsNf0H_9oMRvYvDKJACm0mHylbbq20YYCFBzv1RB88oAxvxANsh3t41kigmuvGshLarDwFbXK96WfrE_t18ogVKl15fCy9DIJb4DZ6KfhmjxJhZ3uTCPpa_rxmLjKWZGjpRVsw2PUed-RDcsgyroCCNehY0qc2wSPZjCUCMZ7N1OCW30Ek5tv_Y8v9_IH7FzG70ILnXVuF3QPvWCy8J6uvYvdQ_GM3sFmQDWypWK_euWxr43oztuEwtgZTIXEZEq4XLlNJwZ1qcDVHsSGHZmx6txBPyoCNCOksk0lVdTwGN9r3GfmWGPZaionGp8Ay4Pd8sT4qStuGu74bq6iL43IguqU1yYEKMuB-GZznthpbUT4JRAwXsooJdtQoMM4vdhMANGW680Ox-kgcAQsL8FdZBygA83sITo8E1twYMAH3P05U-RqhpGs0CRDpoq_tasz6pcxa48sgBenyCqgWGqVDNE5EN6Ma7RWPAMs50IMSEO2TOS0BIxqHgcY7-3dOjyLyNTF7dBNU9Ty_AF_dDpL5Uir_d3J7k0h5VgT8PcqOpML7JEILZmJI8Bi8Z2_XioXS7FDOVjNhJRTnkdH9igbhO-Q4yoxr2P04_wJXgXRxmXyGM_Kjt27sMNE5jpL6EaiSR1isoSqtolakFxfr6cJQfhHhOwSSjxqoR1d4sjq8Z_AES6e8zrFFgehQ9MpdJP7NrrMRtOe6Eg1gzKcfPnocZl_nueCZp3faDEzL9lFpQ0SOUwFQiylV4CuEwf5Ec9vmW2uvK_DC_iEEUDoVOXNr_0pt_Y0N6V569ZKe-mc29bjrBCh8P5SpSEtCY_w4K-dJ1FISLvnIOdvr0qJjsrvXR0BJM5E_8pz8ignWv0YMCJtjRC8m0Pmjdw9eSIYYiS_vzSdRJT4UJnFb1vkHSTt_OhTCXpzaW122orVjkniax-2oQYM3U5B4geSQTiXDOgbkasfNRU2Fcyul552pQzxVmQQDIELCHPhfPnVt-Wpoyw8ZwBgIrajnhp-7LgCiNaV2A2ccnXo2uMTcLgG80eYlzhtLX1X4vol5X2l6U7ojHnd3IFap0d9KpTJ4KWqTEnUQ-SOUYk0U7ep1qkgvMlAZjuuSLgJ.QVLo42D2LC8qXWXI-BI58Q'

			// Treat null/undefined the same as empty string — need fresh backend token
			if ((!tokenFilter || tokenFilter === '') && environment.okta.isEnabled)
				tokenFilter = await tokenVerificationAPI()

			// Return the config with the token appended to the Authorization Header
			newConfig.headers.Accept = 'application/json'
			newConfig.headers.Authorization = `Bearer ${environment?.oidc?.isEnabled ? localStorage.getItem("access_token") : tokenFilter}`
			if (!newConfig.headers['Content-Type']) {
				newConfig.headers['Content-Type'] = 'application/json'
			}

			return newConfig;
		}
		return config;
	};

	const requestInterceptorErrorHandlerFun = (error: any) => {
		requestInterceptorErrorHandler(error);
	}

	/**
	 * Release the ERR_NETWORK latch.
	 *
	 * The latch exists so that while the backend is unreachable we skip the
	 * token-verification round-trip (it would fail anyway) — see the guard in
	 * requestInterceptorSuccessHandler, which returns the config WITHOUT an
	 * Authorization header while latched.
	 *
	 * The correct release signal is "the backend produced ANY HTTP response"
	 * — 200, 401, 404, 500 all prove reachability. Releasing only on 2xx
	 * DEADLOCKS: while latched every request goes out unauthenticated, comes
	 * back 401, and a 401 is an error — so the success handler never runs and
	 * the latch is never released. That is the login-loop this whole guard
	 * caused (auth header silently stripped for the life of the page).
	 */
	const clearNetworkErrorLatch = () => {
		errStatus = null
		if (errorInfo?.status === 'ERR_NETWORK')
			dispatch(setErrorInfo({ status: '', msg: '' }))
	}

	const responseInterceptorSuccessHandlerFun = (response: any) => {
		clearNetworkErrorLatch()
		return responseInterceptorSuccessHandler(response);
	}

	const responseInterceptorErrorHandlerFun = async (error: any) => {
		// `error.response` present => the request REACHED the backend and got a
		// status back, so the network is demonstrably up. Release the latch
		// BEFORE branching on the status, otherwise a 401 (the most likely
		// response while latched, since the header was stripped) leaves it set
		// forever. Trigger seen locally: the background-job poller's
		// OPTIONS /jobs/active preflight 404s when CELERY_ENABLED=false
		// (blueprint unregistered) and the browser surfaces that to axios as
		// ERR_NETWORK.
		if (error?.response) clearNetworkErrorLatch()

		if (error?.response?.status === 401) {
			// A 401 is usually a STALE CREDENTIAL, not a dead session. The JWE we sent
			// wraps one specific Okta access token; once Okta rotates that token the
			// backend's introspection returns active=false and every request 401s until
			// the JWE is re-minted. Logging out here — the behavior since 10fdf08d
			// (2026-05-15) — turns that routine rotation into a mid-work session
			// teardown with no countdown dialog and no "Stay Logged In".
			//
			// So: renew once, evict the stale JWE, replay the request once. Only when
			// renewal fails, or the replay 401s too, is the session genuinely over.
			// canRetryAfter401() withholds the retry from /auth/token-verification —
			// a 401 there is Okta itself rejecting the access token at introspection,
			// so there is nothing left to renew into.
			const config = error?.config
			if (environment?.okta?.isEnabled && oktaAuth && canRetryAfter401(config)) {
				markRetried(config)
				const renewed = await renewAccessTokenOnce(oktaAuth)
				if (renewed) {
					clearBackendToken()
					dispatch(resetEncryptedToken())
					return axios.request(config)
				}
			}
			performLogout(`unrecoverable-401 (url: ${config?.url ?? 'unknown'})`);
		} else if (error?.code === "ERR_NETWORK") {
			// No response object at all — genuinely unreachable. Latch ON.
			errStatus = 'ERR_NETWORK'
			dispatch(setErrorInfo({ status: 'ERR_NETWORK', msg: '' }))
		} else
			errorInfo.status !== '' && dispatch(setErrorInfo({ status: '', msg: '' }))
		return responseInterceptorErrorHandler(error);
	}
	return children;
}
UndecoratedSetupAxios.displayName = "SetupAxios"
export default withRouter(withOktaAuth(UndecoratedSetupAxios));